﻿import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigation } from 'react-router-dom';
import { FaCalendarAlt, FaEye, FaPencilAlt, FaMinusCircle, FaUsers, FaUserPlus, FaSearch } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { Patient, Tab, SortConfig, PaginatedResponse } from '../../types';
import BookAppointmentModal from '../../components/BookAppointmentModal';

interface ProviderOption {
        id: string;
        name: string;
}

const LoadingOverlay = () => (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
                <div className="flex flex-col items-center">
                        <div className="loading loading-spinner loading-lg text-primary"></div>
                        <p className="mt-4 text-white">Loading...</p>
                </div>
        </div>
);

const Patients = () => {
        const navigation = useNavigation();
        const { token } = useAuth();
        const [searchTerm, setSearchTerm] = useState<string>('');
        const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
        const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
        const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: 'ascending' });
        const [activeTab, setActiveTab] = useState<string>('All');
        const [patients, setPatients] = useState<Patient[]>([]);
        const [currentPage, setCurrentPage] = useState<number>(1);
        const [totalCount, setTotalCount] = useState<number>(0);
        const [totalPages, setTotalPages] = useState<number>(1);
        const [pageSize] = useState<number>(10);
        const [providers, setProviders] = useState<ProviderOption[]>([]);
        const [bookingPatient, setBookingPatient] = useState<{ id: string; name: string } | null>(null);
        // Stable across re-renders while the modal is open -- a fresh `new Date()`
        // on every render would re-trigger BookAppointmentModal's defaultDate reset
        // effect and snap the user's chosen date back to today.
        const bookingDefaultDate = useMemo(() => new Date(), [bookingPatient]);

        useEffect(() => {
                const loadProviders = async () => {
                        if (!token) return;
                        try {
                                const response = await apiCall('/providers/providers/?is_active=true', { method: 'GET' }, token);
                                if (!response.ok) return;
                                const data = await response.json();
                                const list = (Array.isArray(data) ? data : data.results || []).map((provider: any) => ({
                                        id: String(provider.id),
                                        name: provider.full_name || `${provider.first_name || ''} ${provider.last_name || ''}`.trim() || 'Provider',
                                }));
                                setProviders(list);
                        } catch (err) {
                                console.error('Error loading providers:', err);
                        }
                };
                loadProviders();
        }, [token]);

        useEffect(() => {
                const timer = setTimeout(() => {
                        setDebouncedSearchTerm(searchTerm);
                }, 300);

                return () => clearTimeout(timer);
        }, [searchTerm]);

        const isLoading = navigation.state === "loading";

        const tabs = [
                { id: 'All', label: 'All' },
                { id: 'Active', label: 'Active' },
                { id: 'Inactive', label: 'Inactive' },
                { id: 'Archived', label: 'Archived' },
        ];

        useEffect(() => {
                const fetchPatients = async () => {
                        if (!token) return;
                        try {
                                const params = new URLSearchParams();
                                params.set('page', String(currentPage));
                                params.set('page_size', String(pageSize));
                                if (debouncedSearchTerm.trim()) {
                                        params.set('search', debouncedSearchTerm.trim());
                                }
                                if (activeTab === 'Active') {
                                        params.set('is_active', 'true');
                                } else if (activeTab === 'Inactive') {
                                        params.set('is_active', 'false');
                                }

                                const response = await apiCall(`/patients/?${params.toString()}`, {
                                        method: 'GET',
                                }, token);
                                if (response.ok) {
                                        const data = await response.json();
                                        setPatients(data.results || []);
                                        setTotalCount(data.count || 0);
                                        setTotalPages(Math.ceil((data.count || 0) / pageSize));
                                }
                        } catch (error) {
                                console.error('Error fetching patients:', error);
                        }
                };
                fetchPatients();
        }, [token, currentPage, pageSize, debouncedSearchTerm, activeTab]);

        useEffect(() => {
                setCurrentPage(1);
        }, [activeTab]);

        useEffect(() => {
                setCurrentPage(1);
        }, [debouncedSearchTerm]);

        const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.checked) {
                        setSelectedPatients(patients.map(patient => patient.id));
                } else {
                        setSelectedPatients([]);
                }
        };

        const handleSelectOne = (patientId: string) => {
                setSelectedPatients(prev => {
                        if (prev.includes(patientId)) {
                                return prev.filter(id => id !== patientId);
                        } else {
                                return [...prev, patientId];
                        }
                });
        };

        const requestSort = (key: keyof Patient) => {
                let direction: 'ascending' | 'descending' = 'ascending';
                if (sortConfig.key === key && sortConfig.direction === 'ascending') {
                        direction = 'descending';
                }
                setSortConfig({ key, direction });
        };

        const formatPhoneNumber = (phone: string): string => {
                if (typeof phone === 'string') {
                        if (phone.startsWith('04')) {
                                return phone.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
                        } else if (phone.startsWith('03')) {
                                return phone.replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2 $3');
                        }
                        return phone;
                }
                return 'N/A';
        };

        const formatDate = (dateStr: string): string => {
                const date = new Date(dateStr);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
        };

        const handlePageChange = (newPage: number) => {
                if (newPage >= 1 && newPage <= totalPages) {
                        setCurrentPage(newPage);
                }
        };

        const getSortedPatients = () => {
                let filteredPatients = [...patients];

                if (activeTab === 'Active') {
                        filteredPatients = filteredPatients.filter(patient => patient.is_active);
                } else if (activeTab === 'Inactive') {
                        filteredPatients = filteredPatients.filter(patient => !patient.is_active);
                }

                if (sortConfig.key) {
                        filteredPatients.sort((a, b) => {
                                const aValue = a[sortConfig.key as keyof Patient];
                                const bValue = b[sortConfig.key as keyof Patient];
                                
                                // Handle null/undefined values
                                if (aValue == null && bValue == null) return 0;
                                if (aValue == null) return 1;
                                if (bValue == null) return -1;
                                
                                if (aValue < bValue) {
                                        return sortConfig.direction === 'ascending' ? -1 : 1;
                                }
                                if (aValue > bValue) {
                                        return sortConfig.direction === 'ascending' ? 1 : -1;
                                }
                                return 0;
                        });
                }

                return filteredPatients;
        };

        return (
                <>
                        {isLoading && <LoadingOverlay />}
                        <div className="p-6 h-full min-h-0 flex flex-col overflow-hidden">
                                <div className="flex items-center justify-between gap-4 mb-6">
                                        <div className="flex items-center gap-3 min-w-0">
                                                <h1 className="text-2xl font-bold text-base-content flex items-center gap-3 shrink-0">
                                                        <FaUsers className="text-primary" />
                                                        Patients • {activeTab.toLowerCase()}
                                                </h1>
                                                <div className="flex items-center gap-2 input input-bordered input-sm w-64 shrink-0">
                                                        <FaSearch className="text-base-content/40 flex-shrink-0 text-xs" />
                                                        <input
                                                                type="text"
                                                                placeholder="Search patients..."
                                                                className="flex-1 bg-transparent outline-none text-sm"
                                                                value={searchTerm}
                                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                        />
                                                </div>
                                                <select className="select select-bordered select-sm shrink-0">
                                                        <option value="">Provider</option>
                                                </select>
                                        </div>
                                        <Link to="/patients/new" className="btn btn-primary btn-sm gap-2 shrink-0">
                                                <FaUserPlus />
                                                New Patient
                                        </Link>
                                </div>

                                <div className="tabs tabs-bordered mb-6">
                                        {tabs.map((tab) => (
                                                <button
                                                        key={tab.id}
                                                        className={`tab tab-lg ${activeTab === tab.id ? 'tab-active' : ''}`}
                                                        onClick={() => setActiveTab(tab.id)}
                                                >
                                                        {tab.label}
                                                        {tab.id !== 'All' && (
                                                                <span className="ml-2 badge badge-sm">
                                                                        {tab.id === 'Active'
                                                                                ? patients.filter(p => p.is_active).length
                                                                                : tab.id === 'Inactive'
                                                                                        ? patients.filter(p => !p.is_active).length
                                                                                        : 0}
                                                                </span>
                                                        )}
                                                </button>
                                        ))}
                                </div>

                                <div className="relative rounded-lg flex-1 min-h-0 overflow-hidden">
                                        <div className="h-full overflow-auto">
                                        <table className="table table-zebra table-fixed w-full bg-base-100/50">
                                                <colgroup>
                                                        <col className="w-12" />
                                                        <col className="w-36" />
                                                        <col className="w-44" />
                                                        <col className="w-44" />
                                                        <col className="w-32" />
                                                        <col className="w-32" />
                                                        <col className="w-64" />
                                                        <col className="w-48" />
                                                        <col className="w-48" />
                                                </colgroup>
                                                <thead>
                                                        <tr>
                                                                <th className="h-12">
                                                                        <input
                                                                                type="checkbox"
                                                                                className="checkbox checkbox-sm"
                                                                                onChange={handleSelectAll}
                                                                                checked={selectedPatients.length === patients.length}
                                                                        />
                                                                </th>
                                                                <th className="h-12">Patient ID</th>
                                                                <th className="h-12">First Name</th>
                                                                <th className="h-12">Last Name</th>
                                                                <th className="h-12">Date of Birth</th>
                                                                <th className="h-12">Gender</th>
                                                                <th className="h-12">Contact</th>
                                                                <th className="h-12">Next/Last Appointment</th>
                                                                <th className="h-12">Commands</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        {getSortedPatients().map((patient) => (
                                                                <tr key={patient.id} className="h-[40px]">
                                                                        <td className="align-middle">
                                                                                <input
                                                                                        type="checkbox"
                                                                                        className="checkbox checkbox-sm"
                                                                                        checked={selectedPatients.includes(patient.id)}
                                                                                        onChange={() => handleSelectOne(patient.id)}
                                                                                />
                                                                        </td>
                                                                        <td className="align-middle">
                                                                                <span className="font-mono text-sm whitespace-nowrap">
                                                                                        {patient.patient_id?.toString().padStart(9, '0')}
                                                                                </span>
                                                                        </td>
                                                                        <td className="align-middle">
                                                                                <Link
                                                                                        to={`/patients/${patient.id}`}
                                                                                        className="hover:underline cursor-pointer block truncate"
                                                                                        title={patient.first_name}
                                                                                >
                                                                                        {patient.first_name}
                                                                                </Link>
                                                                        </td>
                                                                        <td className="align-middle">
                                                                                <span className="block truncate" title={patient.last_name}>
                                                                                        {patient.last_name}
                                                                                </span>
                                                                        </td>
                                                                        <td className="align-middle whitespace-nowrap">{formatDate(patient.date_of_birth)}</td>
                                                                        <td className="align-middle">
                                                                                <span className={`badge badge-sm ${
                                                                                        patient.gender === 'F' ? 'badge-secondary' : 
                                                                                        patient.gender === 'M' ? 'badge-primary' : 
                                                                                        'badge-neutral'
                                                                                }`}>
                                                                                        {patient.gender === 'F' ? 'Female' : 
                                                                                         patient.gender === 'M' ? 'Male' : 
                                                                                         patient.gender || 'Not Specified'}
                                                                                </span>
                                                                        </td>
                                                                        <td className="align-middle">
                                                                                <div className="whitespace-nowrap overflow-hidden text-ellipsis" title={patient.contact_phone}>
                                                                                        {formatPhoneNumber(patient.contact_phone)}
                                                                                </div>
                                                                                <div className="text-sm opacity-60 whitespace-nowrap overflow-hidden text-ellipsis" title={patient.contact_email}>
                                                                                        {patient.contact_email}
                                                                                </div>
                                                                        </td>
                                                                        <td className="align-middle"></td>
                                                                        <td className="align-middle">
                                                                                <div className="flex gap-2 whitespace-nowrap">
                                                                                        <button
                                                                                                type="button"
                                                                                                className="btn btn-ghost btn-xs"
                                                                                                onClick={() => setBookingPatient({
                                                                                                        id: String(patient.id),
                                                                                                        name: `${patient.first_name} ${patient.last_name}`.trim(),
                                                                                                })}
                                                                                        >
                                                                                                <FaCalendarAlt className="w-4 h-4" />
                                                                                        </button>
                                                                                        <Link to={`/patients/${patient.id}`} className="btn btn-ghost btn-xs">
                                                                                                <FaEye className="w-4 h-4" />
                                                                                        </Link>
                                                                                        <Link to={`/patients/edit/${patient.id}`} className="btn btn-ghost btn-xs">
                                                                                                <FaPencilAlt className="w-4 h-4" />
                                                                                        </Link>
                                                                                        <button
                                                                                                className="btn btn-ghost btn-xs"
                                                                                                onClick={() => {/* Handle mark inactive */ }}
                                                                                        >
                                                                                                <FaMinusCircle className="w-4 h-4" />
                                                                                        </button>
                                                                                </div>
                                                                        </td>
                                                                </tr>
                                                        ))}
                                                </tbody>
                                        </table>
                                        </div>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                        <div className="flex justify-center items-center gap-2 mt-4 pb-2 shrink-0">
                                                <button 
                                                        onClick={() => handlePageChange(1)}
                                                        disabled={currentPage === 1}
                                                        className="btn btn-sm"
                                                >
                                                        First
                                                </button>
                                                <button 
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                        className="btn btn-sm"
                                                >
                                                        Previous
                                                </button>
                                                
                                                <span className="px-4">
                                                        Page {currentPage} of {totalPages} ({totalCount.toLocaleString()} patients)
                                                </span>
                                                
                                                <button 
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                        disabled={currentPage === totalPages}
                                                        className="btn btn-sm"
                                                >
                                                        Next
                                                </button>
                                                <button 
                                                        onClick={() => handlePageChange(totalPages)}
                                                        disabled={currentPage === totalPages}
                                                        className="btn btn-sm"
                                                >
                                                        Last
                                                </button>
                                        </div>
                                )}
                        </div>

                        <BookAppointmentModal
                                isOpen={bookingPatient !== null}
                                onClose={() => setBookingPatient(null)}
                                onBooked={() => setBookingPatient(null)}
                                providers={providers}
                                defaultDate={bookingDefaultDate}
                                initialPatientId={bookingPatient?.id || ''}
                                initialPatientName={bookingPatient?.name || ''}
                        />
                </>
        );
};

export default Patients;